import React, { useState, useEffect, useMemo } from "react";
import { Dropdown, Avatar, Typography, Button, Collapse } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useMercuryContext } from "../../context";
import SetTimezone from "../../components/layout/SetTimezone";
import { LogoutButton as Logout } from "shared/components/Components";
import Loading from "shared/components/Loading";
import { matchAdminItem } from "../../common/utils/helpers";
import "./profile.scss";
import SesameAccountDialog from "../../stories/sesame/sesame-request/SesameAccountDialog";
import { failedNotification, successfulNotification } from "../../common/utils/notifications";
import { usePermissions } from "../../hooks/usePermissions";
import { ROUTES } from "../../common/constants/routes";

interface NotificationsProps {
  handleMainMenuSelection: (key: string | null) => void;
}

const Notifications: React.FC<NotificationsProps> = ({
  handleMainMenuSelection,
}) => {
  const { userDetails } = useMercuryContext();
  if (userDetails.notifications.length === 0) {
    return null;
  }

  return (
    <Button
      type="text"
      icon={<BellOutlined />}
      className="profile-menu-item profile-menu-item__notifications"
    >
      <Link to={ROUTES.NOTIFICATIONS} onClick={() => handleMainMenuSelection(null)}>
        Notifications ({userDetails.notifications.length})
      </Link>
    </Button>
  );
};

const UserData: React.FC = () => {
  const { userDetails } = useMercuryContext();
  const { Text } = Typography;

  return (
    <div
      className="top-wrap"
      style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
    >
      {userDetails.picture ? (
        <Avatar size={32} src={userDetails.picture} />
      ) : (
        <Avatar
          size={32}
          style={{
            backgroundColor: stringToColour(userDetails.firstName),
            fontSize: 14,
          }}
        >
          {userDetails.firstName[0]}
          {userDetails.lastName[0]}
        </Avatar>
      )}
      <div
        className="details-wrap"
        style={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}
      >
        <Text strong>{userDetails.firstName}</Text>
        <Text>{userDetails.email}</Text>
      </div>
    </div>
  );
};

function stringToColour(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let colour = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += ("00" + value.toString(16)).substr(-2);
  }
  return colour;
}

interface SettingsProps {
  isMainOpen: boolean;
}

const Settings: React.FC<SettingsProps> = ({ isMainOpen }) => {
  const [activePanel, setActivePanel] = useState<string | undefined>();

  const handlePanelChange = (key: string | string[]) => {
    if (typeof key === "string") {
      setActivePanel(key);
    } else if (Array.isArray(key) && key.length > 0) {
      setActivePanel(key[0]);
    } else {
      setActivePanel(undefined);
    }
  };

  useEffect(() => {
    if (!isMainOpen) {
      setActivePanel(undefined);
    }
  }, [isMainOpen]);

  const items = [
    {
      key: "1",
      label: "Settings",
      children: <SetTimezone />,
    },
  ];

  return (
    <Collapse
      ghost
      className="profile-menu-item profile-menu-item__settings"
      activeKey={activePanel}
      onChange={handlePanelChange}
      items={items}
    />
  );
};

interface AdminAreaProps {
  adminData: {
    adminMenuItems: Array<{
      id: string;
      title: string;
      children: React.ReactNode;
    }>;
    openAdminMenu: (children: React.ReactNode) => void;
  };
  setIsMainOpen: any;
  handleMainMenuSelection: (key: string | null) => void;
}

const AdminArea: React.FC<AdminAreaProps> = ({
  adminData,
  setIsMainOpen,
  handleMainMenuSelection,
}) => {
  const location = useLocation();
  const currentLocation = location.pathname;
  const matchAdminMenuItem = matchAdminItem(adminData.adminMenuItems, currentLocation);

  const items = [
    {
      key: "admin-area",
      label: "Admin Area",
      children: adminData.adminMenuItems.map((item: any) => {
        const isMatching = matchAdminMenuItem?.[0]?.parentId === item.id;

        return (
          <button
            key={item.id}
            onClick={() => {
              setIsMainOpen(false);
              handleMainMenuSelection(null);
              adminData.openAdminMenu(item.children);
            }}
            className={`admin-link ${isMatching ? "admin-link-active" : ""}`}
          >
            {item.title}
          </button>
        );
      }),
    },
  ];

  return <Collapse items={adminData.adminMenuItems.length ? items : []} className="menu admin-menu"/>;
};

interface SesameAreaProps {
  isMainOpen: boolean;
  setIsMainOpen?: any
}

const SesameArea: React.FC<SesameAreaProps> = ({ isMainOpen, setIsMainOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userDetails, gpClient } = useMercuryContext();
  const [activePanel, setActivePanel] = useState<string | undefined>();
  const { hasPermissions } = usePermissions();
  const isActive = /\/sesame(\/\d+)?$/.test(location.pathname)
    ? "admin-link-active"
    : "";

  const handleSubmit = async (data: any, callback: any) => {
    try {
      let endpoint = "/google-gp-v1/sesameAccounts";
      if (
        data.requestType === "Reactivation" ||
        data.requestType === "Suspension"
      ) {
        const { requesterEmail, ...rest } = data;
        data = rest;
        endpoint = "/google-gp-v1/sesameAccounts/match";
      }
      await gpClient.post(endpoint, data);
      callback();
      successfulNotification(
        "Success",
        "Your request has been successfully submitted"
      );
    } catch (error: any) {
      callback();
      failedNotification(
        "Error",
        error?.response?.data?.details?.body?.message ||
          error?.response?.data?.message || error?.response?.data?.error ||
          "Something went wrong"
      );
    }
  };

  useEffect(() => {
    if (!isMainOpen) {
      setActivePanel(undefined);
    }
  }, [isMainOpen]);

  const hasSesameReadAccess = hasPermissions([{ customerApplication: "Google", permissionName: "Sesame Admin", action: "read" }]);

  const items = [
    {
      key: "1",
      label: "Sesame Area",
      children: (
        <>
          {
            hasSesameReadAccess && (
              <Button
                type="text"
                className={`admin-link ${isActive}`}
                onClick={() => {
                  setIsMainOpen(false);
                  navigate(ROUTES.GOOGLE_SESAME);
                }}
              >
                Sesame Account
              </Button>
            )
          }
          
          <SesameAccountDialog
            onSubmit={(data, callback) => handleSubmit(data, callback)}
            requesterEmail={userDetails?.email}
            isMenuItem
            buttonLabel="Make a new Request"
            setIsMainOpen={setIsMainOpen}
          />
        </>
      ),
    },
  ];

  return (
    <Collapse
      ghost
      className="profile-menu-item profile-menu-item__settings"
      items={items}
      activeKey={activePanel}
    />
  );
};

interface ProfileProps {
  adminData: any;
  handleMainMenuSelection: (key: any) => void;
  selectedAppName: string;
}

const UserAvatar: React.FC<any> = ({ userDetails }) => {
  const initials = useMemo(() => {
    const f = userDetails.firstName?.[0] || "";
    const l = userDetails.lastName?.[0] || "";
    const combined = (f + l).toUpperCase();
    return combined || userDetails.firstName?.slice(0, 2).toUpperCase() || "?";
  }, [userDetails.firstName, userDetails.lastName]);

  const bgColor = stringToColour(userDetails.firstName || "User");
  const notifications = userDetails.notifications?.length || 0;

  return (
    <div className="user-avatar">
      {/* Avatar */}
      {userDetails.picture ? (
        <img
          className="user-avatar__image"
          src={userDetails.picture}
          alt={userDetails.firstName}
        />
      ) : (
        <div className="user-avatar__placeholder" style={{ backgroundColor: bgColor }}>
          {initials}
        </div>
      )}

      {/* Badge */}
      {notifications > 0 && (
        <span className="user-avatar__notifications"
        >
          {notifications > 99 ? "99+" : notifications}
        </span>
      )}
    </div>
  );
};

const Profile: React.FC<ProfileProps> = ({
  adminData,
  handleMainMenuSelection,
  selectedAppName,
}) => {
  const { isAuthLoading, userDetails, flags } = useMercuryContext();
  const [isMainOpen, setIsMainOpen] = useState(false);

  if (isAuthLoading) {
    return <Loading />;
  }

  const toggleMainMenu = (open:boolean, info:any) => {
    if (info?.source === 'trigger') {
      setIsMainOpen(open);
    }
  };

  const items = [
    { label: <UserData />, key: "0" },
    {
      label: (
        <Notifications handleMainMenuSelection={handleMainMenuSelection} />
      ),
      key: "1",
    },
    {
      label: (
        <AdminArea
          adminData={adminData}
          setIsMainOpen={setIsMainOpen}
          handleMainMenuSelection={handleMainMenuSelection}
        />
      ),
      key: "3",
    },
    { label: <Settings isMainOpen={isMainOpen} />, key: "4" },
    flags?.sesameAccount &&  
    selectedAppName === "Google" && {
      label: <SesameArea isMainOpen={isMainOpen} setIsMainOpen={setIsMainOpen}/>,
      key: "5",
    },
    { label: <Logout />, key: "6" },
  ].filter(Boolean);

  return (
    userDetails.email && (
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        placement="bottomRight"
        onOpenChange={toggleMainMenu}
        open={isMainOpen}
        overlayClassName="profile-menu"
      >
        <button
          className="ant-dropdown-link profile-wrap"
          onClick={(e) => e.preventDefault()}
          style={{ padding: "0 10px" }}
        >
          <UserAvatar userDetails={userDetails}/>
        </button>
      </Dropdown>
    )
  );
};

export default Profile;
